import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef, inject,
  Input,
  OnDestroy,
  OnInit, signal,
  ViewChild,
} from '@angular/core';
import {ICompany, IUser} from '../../../../../assets/types/dtoTypes';
import {DecimalPipe, NgIf} from "@angular/common";
import {MatCard} from "@angular/material/card";
import {AdminDashboardPageService} from "../admin-dashboard-page.service";
import {Chart, ChartConfiguration, registerables} from 'chart.js';
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";
import {TranslateModule} from "@ngx-translate/core";
import {ReactiveFormsModule} from "@angular/forms";

Chart.register(...registerables);

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss'],
  imports: [
    DecimalPipe,
    MatCard,
    LoadMaskComponent,
    NgIf,
    TranslateModule,
    ReactiveFormsModule,
  ]
})
export class DashboardPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() users: IUser[] = [];
  @Input() companies: ICompany[] = [];
  private readonly adminService = inject(AdminDashboardPageService);
  isLoading = true;
  private viewReady = false;
  private readonly cdr = inject(ChangeDetectorRef);

  totalCompanies = signal<number>(0);
  totalUsers = signal<number>(0);

  @ViewChild('usersGrowthCanvas') usersGrowthCanvas?: ElementRef<HTMLCanvasElement>;
  private usersChart?: Chart;

  range: '7d' | '30d' | '90d' = '30d';

  ngOnInit(): void {
    this.adminService.getAllComapanies().subscribe((companies) => {
      this.totalCompanies.set(companies.length);
    });

    this.adminService.getAllUsers().subscribe((users) => {
      this.users = users;
      this.totalUsers.set(users.length);
      this.isLoading = false;
      this.cdr.detectChanges();
      this.renderUsersChart();
    });

  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderUsersChart();
  }


  ngOnDestroy(): void {
    this.usersChart?.destroy();
  }

  /** ---------------- Chart ---------------- */

  private renderUsersChart(): void {

    if (!this.viewReady) return; // 👈 guard
    const ctx = this.usersGrowthCanvas?.nativeElement.getContext('2d');
    if (!ctx) return;

    if (!this.users.length) {
      this.drawChart(ctx, [], [], 'User Signups');
      return;
    }

    // collect valid dates
    const allDates = this.users
      .map(u => new Date(u.createdAt))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    if (!allDates.length) {
      this.drawChart(ctx, [], [], 'User Signups');
      return;
    }

    const earliest = allDates[0];
    const today = new Date();

    // Build every day from earliest -> today (UTC to avoid TZ off-by-one)
    const labels = this.generateLabelsBetweenUTC(earliest, today);

    // Count signups per UTC day key
    const counts = new Map<string, number>();
    for (const u of this.users) {
      const key = this.toYYYYMMDDUTC(new Date(u.createdAt));
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    // Cumulative series
    const values: number[] = [];
    let cumulative = 0;
    for (const key of labels) {
      cumulative += counts.get(key) ?? 0;
      values.push(cumulative);
    }

    this.drawChart(ctx, labels, values, 'Total User Growth');
  }

  private drawChart(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    values: number[],
    subtitle?: string
  ): void {
    const canvas = this.usersGrowthCanvas?.nativeElement;
    if (!canvas) return;

    // ✔ hard-kill any chart bound to this canvas
    const existing = Chart.getChart(canvas);
    if (existing) existing.destroy();

    // (optional) also clear your own ref
    this.usersChart?.destroy();
    this.usersChart = undefined;

    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, 'rgba(255, 206, 86, 0.45)');
    gradient.addColorStop(1, 'rgba(255, 206, 86, 0.05)');

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'User Growth',
          data: values,
          tension: 0.25,
          borderWidth: 2,
          borderColor: '#FFD54F',
          backgroundColor: gradient,
          pointRadius: 2,
          fill: true,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {display: false},
          title: {display: !!subtitle, text: subtitle || ''},
          tooltip: {
            callbacks: {
              title(items) {
                const raw = items[0]?.label ?? '';
                return new Date(raw).toDateString();
              },
              label(item) {
                return `Total: ${item.parsed.y}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: 'category',
            grid: {display: false},
            ticks: {
              autoSkip: true,
              maxRotation: 0,
              callback: function (_v, i, ticks) {
                return i % Math.ceil(ticks.length / 6) === 0 ? (labels[i] as string) : '';
              },
            },
          },
          y: {
            beginAtZero: true,
            grid: {color: 'rgba(0,0,0,0.06)'},
          },
        },
      },
    };

    this.usersChart = new Chart(canvas, config);
  }


  /** --------- helpers --------- */
  private generateLastNDaysLabels(n: number): string[] {
    const result: string[] = [];
    const base = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(base.getTime() - i * 86400000);
      result.push(this.toYYYYMMDD(d));
    }
    return result;
  }

  private toYYYYMMDD(d: Date): string {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private generateLabelsBetweenUTC(start: Date, end: Date): string[] {
    const out: string[] = [];
    // normalize to UTC midnight
    let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
    const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));

    while (cursor.getTime() <= endUTC.getTime()) {
      out.push(this.toYYYYMMDDUTC(cursor));
      cursor = new Date(cursor.getTime() + 86400000); // +1 day
    }
    return out;
  }

  private toYYYYMMDDUTC(d: Date): string {
    if (isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }


}
